import {useTranslation} from 'react-i18next';
import {SVG} from '../../../../../constants/assetLists';
import {useOperationsContext} from '../../../../../context/OperationsContextProvider';
import React from 'react';
import useScreenSize from '../../../../../hooks/useScreenSize';
import {getCorrectPath, isAdmin, isSuperAdmin} from '../../../../../utils';
import {ICONS} from '../../../../users/data';
import AppMUIAutocomplete from '../../../../../components/common/AppMUIAutoComplete';
import {
  BranchData,
  ClientDataType,
  SubBranchData,
  useClientsContext,
} from '../../../../../context/clients/ClientsContextProvider';

export interface FilterItem {
  type: string;
  text: string;
  icon: string;
  count: number;
}

type LocationOption = {
  label: string;
  value: string;
  nodeType: 'client' | 'branch' | 'subBranch';
  clientId: string;
  branchId?: string;
};

// Some field shapes vary (single id vs. array of ids) depending on the API response —
// handle both so this doesn't silently break if the backend shape differs.
const matchesId = (field: any, id?: string) => {
  if (!id) return false;
  if (Array.isArray(field)) return field.includes(id);
  return field === id;
};

const isEmptyField = (field: any) =>
  Array.isArray(field) ? field.length === 0 : !field;

function deriveLocationOption(
  selectedLocation: ClientDataType | BranchData | SubBranchData | null,
): LocationOption | null {
  if (!selectedLocation) return null;

  if (selectedLocation.isClient) {
    return {
      label: selectedLocation.name,
      value: selectedLocation.id,
      nodeType: 'client',
      clientId: selectedLocation.id,
    };
  }

  if (selectedLocation.isBranch) {
    return {
      label: selectedLocation.name,
      value: selectedLocation.id,
      nodeType: 'branch',
      clientId: (selectedLocation as BranchData).clientId,
      branchId: selectedLocation.id,
    };
  }

  const sub = selectedLocation as SubBranchData;
  return {
    label: selectedLocation.name,
    value: selectedLocation.id,
    nodeType: 'subBranch',
    clientId: sub.clientId,
    branchId: sub.branchId,
  };
}

function FilterSidebar() {
  const {i18n, t} = useTranslation();
  const {allClients, selectedLocation} = useClientsContext();
  const {
    filterSidebarVariant,
    setFilterSidebarVariant,
    announcementFilterType,
    setAnnouncementFilterType,
    setAnnouncementFilterLocation,
    setAnnouncementFilterLocationLabel,
    setCompactSidebarWidth,
    announcementStatsData,
  } = useOperationsContext();

  const screenSize = useScreenSize();
  const isSmall = screenSize !== 'xl' && screenSize !== '2xl';

  const sidebarRef = React.useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = React.useState(false);

  const [filterLocation, setFilterLocation] =
    React.useState<LocationOption | null>(() =>
      deriveLocationOption(selectedLocation),
    );

  React.useEffect(() => {
    const nextLocation = deriveLocationOption(selectedLocation);
    setFilterLocation(nextLocation);
    setAnnouncementFilterLocation(
      nextLocation
        ? {
            label: nextLocation.label,
            nodeType: nextLocation.nodeType,
            clientId: nextLocation.clientId,
            branchId: nextLocation.branchId,
            subBranchId:
              nextLocation.nodeType === 'subBranch'
                ? nextLocation.value
                : undefined,
          }
        : null,
    );
    setAnnouncementFilterLocationLabel(nextLocation?.label ?? '');
  }, [
    selectedLocation,
    setAnnouncementFilterLocation,
    setAnnouncementFilterLocationLabel,
  ]);

  const [user] = React.useState(() => {
    const s = localStorage.getItem('user');
    return s && s !== 'undefined' ? JSON.parse(s) : null;
  });

  const userDepartmentIds = React.useMemo(() => {
    if (isSuperAdmin() || isAdmin()) return null;
    if (!user?.departmentId || user.departmentId === 'all') return null;
    if (Array.isArray(user.departmentId)) {
      return user.departmentId.map((d: any) => d._id);
    }
    return [user.departmentId?._id || user.departmentId];
  }, [user]);

  const FILTERS = React.useMemo(() => {
    const statsData = announcementStatsData?.data;

    if (!statsData?.byDepartment) {
      return [
        {
          type: 'all',
          text: t('הכל'),
          icon: SVG.filterSidebarAllIcon,
          count: statsData?.total ?? 0,
        },
      ];
    }

    const matchesLocation = (dept: any) => {
      if (!filterLocation) return true;

      if (filterLocation.nodeType === 'client') {
        return (
          matchesId(dept.departmentClientId, filterLocation.clientId) &&
          isEmptyField(dept.departmentBranchId)
        );
      }

      if (filterLocation.nodeType === 'branch') {
        return (
          matchesId(dept.departmentClientId, filterLocation.clientId) &&
          matchesId(dept.departmentBranchId, filterLocation.value) &&
          isEmptyField(dept.departmentSubBranchId)
        );
      }

      // subBranch
      return (
        matchesId(dept.departmentClientId, filterLocation.clientId) &&
        matchesId(dept.departmentBranchId, filterLocation.branchId) &&
        matchesId(dept.departmentSubBranchId, filterLocation.value)
      );
    };

    let departments: FilterItem[] = statsData.byDepartment
      .filter((dept: any) => dept.departmentId && dept.departmentId !== 'all')
      .filter(matchesLocation)
      .map((dept: any) => ({
        type: dept.departmentId,
        text: dept.departmentName || dept.departmentId,
        icon: dept.departmentIcon
          ? dept.departmentIcon.startsWith('icon')
            ? ICONS.find(icon => icon.id === dept.departmentIcon)?.icon
            : getCorrectPath(dept.departmentIcon, 'image')
          : SVG.filterSidebarAllIcon,
        count: dept.count ?? 0,
      }));

    if (userDepartmentIds) {
      departments = departments.filter((d: FilterItem) =>
        userDepartmentIds.includes(d.type),
      );
    }

    departments.sort((a, b) => a.text.localeCompare(b.text, 'he'));

    const allEntry: FilterItem = {
      type: 'all',
      text: t('הכל'),
      icon: SVG.filterSidebarAllIcon,
      count: statsData.total ?? 0,
    };

    return [allEntry, ...departments];
  }, [announcementStatsData, t, userDepartmentIds, filterLocation]);

  const locationOptions = React.useMemo(() => {
    const options: LocationOption[] = [];
    if (!selectedLocation) return options;

    const clientId = selectedLocation.isClient
      ? selectedLocation.id
      : selectedLocation.clientId;

    const client = allClients?.find((c: ClientDataType) => c.id === clientId);
    if (!client) return options;

    const pushSubBranches = (branch: BranchData) => {
      branch.subBranches?.forEach((subBranch: SubBranchData) => {
        options.push({
          label: subBranch.name,
          value: subBranch.id,
          nodeType: 'subBranch',
          clientId: client.id,
          branchId: branch.id,
        });
      });
    };

    // Only show the subtree at and below whatever level is currently selected.
    if (selectedLocation.isClient) {
      options.push({
        label: client.name,
        value: client.id,
        nodeType: 'client',
        clientId: client.id,
      });

      client.branches?.forEach((branch: BranchData) => {
        options.push({
          label: branch.name,
          value: branch.id,
          nodeType: 'branch',
          clientId: client.id,
          branchId: branch.id,
        });
        pushSubBranches(branch);
      });

      return options;
    }

    if (selectedLocation.isBranch) {
      const branch = client.branches?.find(
        (b: BranchData) => b.id === selectedLocation.id,
      );
      if (!branch) return options;

      options.push({
        label: branch.name,
        value: branch.id,
        nodeType: 'branch',
        clientId: client.id,
        branchId: branch.id,
      });
      pushSubBranches(branch);

      return options;
    }

    // isSubBranch
    const sub = selectedLocation as SubBranchData;
    const branch = client.branches?.find(
      (b: BranchData) => b.id === sub.branchId,
    );
    const subBranch = branch?.subBranches?.find(
      (s: SubBranchData) => s.id === sub.id,
    );
    if (branch && subBranch) {
      options.push({
        label: subBranch.name,
        value: subBranch.id,
        nodeType: 'subBranch',
        clientId: client.id,
        branchId: branch.id,
      });
    }

    return options;
  }, [allClients, selectedLocation]);

  const selectedLocationOption = filterLocation;

  const handleLocationChange = (
    _e: React.SyntheticEvent,
    value: LocationOption | null,
  ) => {
    setFilterLocation(value);
    setAnnouncementFilterLocation(
      value
        ? {
            label: value.label,
            nodeType: value.nodeType,
            clientId: value.clientId,
            branchId: value.branchId,
            subBranchId:
              value.nodeType === 'subBranch' ? value.value : undefined,
          }
        : null,
    );
    setAnnouncementFilterLocationLabel(value?.label ?? '');
    setAnnouncementFilterType('all');
  };

  const isHebrew = React.useMemo(
    () => i18n.language === 'heb',
    [i18n.language],
  );
  const isFull = React.useMemo(
    () => filterSidebarVariant === 'full',
    [filterSidebarVariant],
  );
  const isCompact = React.useMemo(
    () => filterSidebarVariant === 'compact',
    [filterSidebarVariant],
  );
  const hasLocationOptions = locationOptions.length > 1;

  React.useEffect(() => {
    if (isSmall) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !sidebarRef.current) return;

      const newWidth = isHebrew ? window.innerWidth - e.clientX : e.clientX;

      if (newWidth >= 215) {
        setFilterSidebarVariant('full');
        setIsDragging(false);
        return;
      }

      const width = `${Math.max(newWidth, 66)}px`;
      setCompactSidebarWidth(width);
      sidebarRef.current.style.width = width;
    };

    const stopDragging = () => setIsDragging(false);

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', stopDragging);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stopDragging);
    };
  }, [
    isDragging,
    isHebrew,
    isSmall,
    setFilterSidebarVariant,
    setCompactSidebarWidth,
  ]);

  React.useEffect(() => {
    if (isSmall || !sidebarRef.current) return;

    if (isCompact) {
      setCompactSidebarWidth('66px');
    }

    sidebarRef.current.style.width = isFull
      ? '215px'
      : isCompact
        ? '66px'
        : '24px';
  }, [
    filterSidebarVariant,
    isSmall,
    isCompact,
    isFull,
    setCompactSidebarWidth,
  ]);

  const handleSidebarWidthChange = () => {
    setFilterSidebarVariant(prev =>
      prev === 'full' ? 'compact' : prev === 'compact' ? 'hidden' : 'full',
    );
  };

  const handleMouseDown = () => {
    if (isSmall) return;
    setIsDragging(true);
  };

  const handleFilterTypeChange = (type: string) => {
    setAnnouncementFilterType(type);
    if (isSmall) {
      setIsMobileSidebarOpen(false);
    }
  };

  if (isSmall) {
    return (
      <div className="flex flex-col bg-[#f4f4f4] border-b border-b-lightDarkGray px-3 py-2 min-w-full">
        <div className="flex justify-between items-center">
          <span className="text-[20px] font-light leading-6 text-black">
            תצוגה
          </span>
          <button
            className="w-[20px] h-[20px]"
            onClick={() => setIsMobileSidebarOpen(prev => !prev)}>
            <img
              src={isMobileSidebarOpen ? SVG.expandIcon : SVG.lessThanIcon}
              alt="toggle filter"
              className="w-full h-full object-contain"
            />
          </button>
        </div>

        {isMobileSidebarOpen && (
          <div className="flex overflow-x-auto gap-2 py-2 no-scrollbar">
            {FILTERS.map((filter: FilterItem, index: number) => (
              <button
                key={index}
                className={`flex-none flex items-center px-4 py-2 rounded-full transition-colors duration-200 whitespace-nowrap ${
                  announcementFilterType === filter.type
                    ? 'bg-softAqua'
                    : 'bg-gray-200'
                }`}
                onClick={() => handleFilterTypeChange(filter.type)}>
                <div className="flex items-center gap-2">
                  <div className="w-[20px] h-[20px]">
                    <img
                      src={filter.icon}
                      alt="icon"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <span className="text-black">{filter.text}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={sidebarRef}
      className={`flex-none relative transition-all duration-150 h-full bg-[#f4f4f4] ${
        i18n.language == 'heb'
          ? `border-l-2 ${isDragging ? 'border-l-[#007AFF]' : 'border-l-black'}`
          : `border-r-2 ${isDragging ? 'border-r-[#007AFF]' : 'border-r-black'}`
      }`}>
      <div
        className={`${
          filterSidebarVariant !== 'hidden' && 'px-3'
        } py-3 border-b border-b-lightDarkGray`}>
        <div
          className={`flex ${
            isFull ? 'justify-between' : 'justify-center'
          }  items-center`}>
          {isFull && (
            <span className="text-[20px] font-light leading-6 text-black">
              תצוגה
            </span>
          )}

          <button
            className="w-[12px] h-[12px] hover:opacity-40"
            onClick={handleSidebarWidthChange}>
            <img
              src={
                filterSidebarVariant == 'hidden'
                  ? SVG.lessThanIcon
                  : SVG.greaterThanIcon
              }
              alt="add"
              className="w-full h-full object-contain"
            />
          </button>
        </div>
      </div>

      <div className="w-full h-full">
        {hasLocationOptions && (
          <div className={`w-full px-3 pt-3`}>
          {isFull && (
            <AppMUIAutocomplete
              label="צפייה במידע של"
              options={locationOptions}
              getOptionLabel={option => option.label}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              size="medium"
              fullWidth
              value={filterLocation as LocationOption}
              onChange={handleLocationChange}
              disableClearable
            />
          )}
          </div>
        )}

        {(isFull || isCompact) && (
          <div
            className={`w-full h-full flex flex-col gap-2 px-3 py-2 overflow-y-auto`}>
            {FILTERS.map((filter: FilterItem, index: number) => (
              <button
                key={index}
                className={`flex items-center px-2 py-2 transition-colors duration-200 hover:bg-hoverBg
                  ${isFull ? 'justify-between' : 'justify-center'} ${
                    isHebrew
                      ? 'rounded-tl-[30px] rounded-bl-[30px] rounded-tr-[6px] rounded-br-[6px]'
                      : 'rounded-tr-[30px] rounded-br-[30px] rounded-tl-[6px] rounded-bl-[6px]'
                  } ${announcementFilterType == filter.type && 'bg-softAqua'}`}
                onClick={() => {
                  handleFilterTypeChange(filter.type);
                }}>
                <div className="flex items-center gap-2">
                  <div className="w-[20px] h-[20px]">
                    <img
                      src={filter.icon}
                      alt="icon"
                      className="w-full h-full object-contain"
                    />
                  </div>

                  {isFull && <span className="text-black">{filter.text}</span>}
                </div>

                {isFull && <span className="text-black">{filter.count}</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {isCompact && (
        <div
          className="absolute top-0 bottom-0 w-[6px] cursor-col-resize z-1"
          style={{
            [i18n.language === 'heb' ? 'left' : 'right']: 0,
          }}
          onMouseDown={handleMouseDown}
        />
      )}
    </div>
  );
}

export default FilterSidebar;
